import type { IconType } from 'react-icons';
import { FaCog, FaExchangeAlt, FaGift, FaHistory, FaUserFriends, FaWallet } from 'react-icons/fa';
import { FiDownload, FiRefreshCw, FiX } from 'react-icons/fi';
import { TbWorld } from 'react-icons/tb';
import { ImProfile } from 'react-icons/im';
import { HiMiniKey } from 'react-icons/hi2';
import { BiLogOutCircle } from 'react-icons/bi';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '@/store/api/authApi';
import { toast } from 'sonner';
import { ApiError } from '@/types/error';
import { logout } from '@/store/slices/authSlice';
import { IoGlobeSharp } from 'react-icons/io5';
import { useParlay } from '@/hooks/useParlay';
import { clearBetCount } from '@/store/slices/currentBetCountSlice';
import { GrAnnounce } from 'react-icons/gr';
import { clearStoredParlaySelections } from '@/utils/parlaySelectionDb';
import SupportCard from './SupportCard';
import { useInstallApp } from '@/hooks/useInstallApp';

type SidebarIcon = IconType | string;

interface AccountMenuItem {
  label: string;
  icon: SidebarIcon;
  badge?: number;
  href: string;
}

interface SidebarMenuProps {
  open: boolean;
  onClose: () => void;
}

// Order below intentionally mirrors the reference design's menu order for
// every item that has an equivalent here (Balance Overview → ... →
// Referral). Items we have that the reference doesn't (My Wallet,
// Promotions, Account Statement, Turnover History, Bonus) are appended
// after, so they still show up, just below the matched set.
const accountMenus: AccountMenuItem[] = [
  {
    label: 'Switch Wallet',
    icon: FiRefreshCw,
    href: '/switch-wallet',
  },
  {
    label: 'Balance Overview',
    icon: FaWallet,
    href: '/balance-overview',
  },
  {
    label: 'Current Bets',
    icon: 'icon-list',
    badge: 0,
    href: '/current-bets',
  },
  {
    label: 'Bets History',
    icon: FaHistory,
    href: '/bets-history',
  },
  {
    label: 'Profit & Loss',
    icon: 'icon-profit-loss',
    href: '/profit-loss',
  },
  {
    label: 'Activity Log',
    icon: HiMiniKey,
    href: '/activity-log',
  },
  {
    label: 'My Profile',
    icon: ImProfile,
    href: '/profile',
  },
  {
    label: 'Referral',
    icon: FaUserFriends,
    href: '/newreferral',
  },
  // My Wallet — not needed for now, commented out per request.
  // {
  //   label: 'My Wallet',
  //   icon: FaWallet,
  //   href: '/wallet',
  // },
  {
    label: 'Promotions',
    icon: GrAnnounce,
    href: '/promotions',
  },
  {
    label: 'Account Statement',
    icon: 'icon-list',
    href: '/account-statement',
  },
  {
    label: 'My Requests',
    icon: FaExchangeAlt,
    href: '/my-requests',
  },
  {
    label: 'Turnover History',
    icon: FaHistory,
    href: '/turnover-history',
  },
  {
    label: 'Bonus',
    icon: FaGift,
    href: '/bonus-statement',
  },
];

const menuIconClassName = 'inline-flex w-6 shrink-0 justify-center leading-none text-[#12263A]';

function MenuIcon({ icon }: { icon: SidebarIcon }) {
  if (typeof icon === 'string') {
    return <i className={`${icon} ${menuIconClassName} !text-[1.25rem]`} />;
  }

  const Icon = icon;
  return <Icon className={`${menuIconClassName} text-[1.125rem]`} />;
}

export default function SidebarMenu({ open, onClose }: SidebarMenuProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setIsParlayEnabled } = useParlay();
  const { isInstalled, handleInstall } = useInstallApp();
  const [logoutApi] = useLogoutMutation();
  const currentBetCount = useAppSelector((state) => state.currentBetCount.totalItems);
  const currentBetCountText = currentBetCount > 99 ? '99+' : currentBetCount;

  const goTo = (href: string) => {
    navigate(href);
    onClose?.();
  };
  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      const message = (error as ApiError)?.data?.message || 'Logout failed';
      toast.error(message);
    } finally {
      localStorage.removeItem('showWelcomeBanner');
      setIsParlayEnabled(false);
      dispatch(logout());
      dispatch(clearBetCount());
      clearStoredParlaySelections();
      navigate('/', { replace: true });
      onClose?.();
    }
  };

  return (
    <div
      className={`h-full fixed z-[140] overflow-hidden bottom-0 top-0 max-w-[30rem] w-full mx-auto ${
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <aside
        className={`relative left-0 top-0 h-full w-3/4 overflow-y-auto bg-[#E9EFF3] px-2.5 md:px-4 py-3 transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between px-1">
          <h2 className="text-[1.125rem] font-bold text-[#12263A]">Menu</h2>

          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center">
            <FiX className="text-[1.625rem] text-[#12263A]" />
          </button>
        </div>

        {/* Account Section */}
        <div className="overflow-hidden rounded-[0.875rem] bg-white">
          {accountMenus.map((item, index) => {
            const isActive = pathname === item.href;

            return (
              <button
                key={item.label}
                className={`flex gap-2 h-[3.5rem] w-full items-center px-3 sm:px-4 ${
                  index !== accountMenus.length - 1 ? 'border-b border-[#D7DEE5]' : ''
                } ${isActive ? 'bg-primary/35' : ''}`}
                onClick={() => goTo(item.href)}
              >
                <MenuIcon icon={item.icon} />

                <span className="flex flex-1 items-center text-base sm:text-[1.156rem] text-[#12263A]">
                  {item.label}

                  {item.badge !== undefined && (
                    <span className="ml-2 flex items-center justify-center rounded bg-[#F3C400] px-1.5 text-[1.156rem] font-medium text-black">
                      {currentBetCountText}
                    </span>
                  )}
                </span>

                <i className="icon-arrow-down opacity-50 -rotate-90 text-[0.98rem]" />
              </button>
            );
          })}
        </div>

        {/* Settings */}
        <div className="mt-[1.125rem] overflow-hidden rounded-[0.875rem] bg-white">
          <button
            onClick={() => goTo('/setting')}
            className={`flex gap-2 h-[3.375rem] w-full items-center px-4 ${
              pathname === '/setting' ? 'bg-primary/35' : ''
            }`}
          >
            <FaCog className={menuIconClassName} />

            <span className="flex-1 text-left text-[1rem] font-medium text-[#12263A]">Setting</span>

            <i className="icon-arrow-down opacity-50 -rotate-90 text-[0.98rem]" />
          </button>
        </div>

        {/* Install app */}
        {!isInstalled && (
          <div className="mt-[1.125rem] overflow-hidden rounded-[0.875rem] bg-white">
            <button
              onClick={() => {
                onClose();
                void handleInstall();
              }}
              className="flex gap-2 h-[3.375rem] w-full items-center px-4"
            >
              <FiDownload className={menuIconClassName} />
              <span className="flex-1 text-left text-[1rem] font-medium text-[#12263A]">Install App</span>
              <i className="icon-arrow-down opacity-50 -rotate-90 text-[0.98rem]" />
            </button>
          </div>
        )}

        {/* Logout */}
        <div className="mt-[1.125rem] overflow-hidden rounded-[0.875rem] bg-[#D6E0E7]">
          <button
            onClick={handleLogout}
            className="flex gap-2 h-[3.375rem] w-full items-center px-4"
          >
            <BiLogOutCircle className="size-[1.6rem]" />

            <span className="flex-1 text-left text-[1rem] font-medium text-[#12263A]">Logout</span>

            <i className="icon-arrow-down opacity-50 -rotate-90 text-[0.98rem]" />
          </button>
        </div>

        <SupportCard />

        {/* Timezone */}
        <div className="mt-8 flex items-center justify-center gap-2">
          <TbWorld stroke="20" className="text-[1.125rem] text-[#6F8498]" />

          <p className="text-[1rem] font-medium text-[#6F8498] flex gap-2 items-center justify-center">
            <IoGlobeSharp className="text-[1.4rem]" />
            Time Zone : GMT+6 (Bangladesh)
          </p>
        </div>
      </aside>
    </div>
  );
}
